import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, UserCheck, UserX, X, Loader2, Users, Pencil } from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '@/lib/api';

const ROLES = [
  { value: 'doctor',          label: 'Doctor' },
  { value: 'nurse',           label: 'Nurse' },
  { value: 'receptionist',    label: 'Receptionist' },
  { value: 'lab_technician',  label: 'Lab Technician' },
  { value: 'pharmacist',      label: 'Pharmacist' },
  { value: 'billing_staff',   label: 'Billing Staff' },
  { value: 'accountant',      label: 'Accountant' },
  { value: 'radiologist',     label: 'Radiologist' },
] as const;

type RoleValue = typeof ROLES[number]['value'];

const ROLE_COLORS: Record<string, string> = {
  doctor:         'bg-blue-50 text-clinical-info',
  nurse:          'bg-purple-50 text-purple-600',
  receptionist:   'bg-orange/10 text-orange-dark',
  lab_technician: 'bg-yellow-50 text-yellow-700',
  pharmacist:     'bg-green-50 text-clinical-success',
  billing_staff:  'bg-pink-50 text-pink-600',
  accountant:     'bg-indigo-50 text-indigo-600',
  radiologist:    'bg-teal-50 text-teal-600',
  hospital_admin: 'bg-navy/10 text-navy',
};

const roleLabel = (r: string) => ROLES.find(x => x.value === r)?.label ?? r.replace('_', ' ');

const PRESCRIPTION_MODES = [
  { value: 'MANUAL',  label: 'Manual',  description: 'Doctor handwrites on pre-printed sheet' },
  { value: 'DIGITAL', label: 'Digital', description: 'System prints the full prescription' },
] as const;

const createSchema = z.object({
  name:             z.string().min(2, 'Required'),
  email:            z.string().email('Invalid email'),
  mobile:           z.string().regex(/^\d{10}$/, '10-digit number'),
  role:             z.string().min(1, 'Select role'),
  specialization:   z.string().optional(),
  regNumber:        z.string().optional(),
  prescriptionMode: z.enum(['MANUAL', 'DIGITAL']).optional(),
  password:         z.string().min(8, 'Minimum 8 characters'),
});
type CreateForm = z.infer<typeof createSchema>;

const editSchema = z.object({
  name:             z.string().min(2, 'Required'),
  mobile:           z.string().regex(/^\d{10}$/, '10-digit number'),
  role:             z.string().min(1, 'Select role'),
  specialization:   z.string().optional(),
  regNumber:        z.string().optional(),
  prescriptionMode: z.enum(['MANUAL', 'DIGITAL']).optional(),
});
type EditForm = z.infer<typeof editSchema>;

// ── Multi-role chip picker ────────────────────────────────────────
function RoleChips({
  primaryRole, selected, onChange,
}: { primaryRole: string; selected: string[]; onChange: (v: string[]) => void }) {
  function toggle(role: string) {
    if (role === primaryRole) return; // primary can't be additional
    onChange(
      selected.includes(role)
        ? selected.filter(r => r !== role)
        : [...selected, role]
    );
  }
  return (
    <div className="flex flex-wrap gap-2 mt-1">
      {ROLES.map(r => {
        const isPrimary  = r.value === primaryRole;
        const isSelected = selected.includes(r.value) || isPrimary;
        return (
          <button
            key={r.value}
            type="button"
            disabled={isPrimary}
            onClick={() => toggle(r.value)}
            className={clsx(
              'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
              isPrimary
                ? 'bg-navy text-white border-navy cursor-default'
                : isSelected
                  ? 'bg-orange text-navy border-orange'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-orange hover:text-orange'
            )}>
            {r.label}
            {isPrimary && <span className="ml-1 opacity-60">(primary)</span>}
          </button>
        );
      })}
    </div>
  );
}

// ── Edit User Modal ───────────────────────────────────────────────
function EditUserModal({ user, onClose }: { user: any; onClose: () => void }) {
  const qc = useQueryClient();
  const [apiError, setApiError]       = useState<string | null>(null);
  const [additionalRoles, setAdditional] = useState<string[]>(
    (() => {
      try {
        const ar = user.additional_roles;
        return Array.isArray(ar) ? ar : JSON.parse(ar ?? '[]');
      } catch { return []; }
    })()
  );

  const { register, handleSubmit, watch, formState: { errors } } = useForm<EditForm>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      name:             user.name,
      mobile:           user.mobile,
      role:             user.role,
      specialization:   user.specialization ?? '',
      regNumber:        user.reg_number ?? '',
      prescriptionMode: (user.prescription_mode as 'MANUAL' | 'DIGITAL') ?? 'MANUAL',
    },
  });

  const watchRole = watch('role');

  const mut = useMutation({
    mutationFn: (body: object) => api.patch(`/users/${user.id}`, body),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['users'] }); onClose(); },
    onError:    (e: any) => setApiError(e.response?.data?.error ?? 'Update failed'),
  });

  function onSubmit(values: EditForm) {
    setApiError(null);
    const extra = additionalRoles.filter(r => r !== values.role);
    mut.mutate({ ...values, additionalRoles: extra });
  }

  const field = 'w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange';
  const lbl   = 'block text-sm font-medium text-navy mb-1.5';
  const errC  = 'text-clinical-danger text-xs mt-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-bold text-navy">Edit User</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-navy">
            <X size={20}/>
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          <div>
            <label className={lbl}>Full Name *</label>
            <input {...register('name')} className={field}/>
            {errors.name && <p className={errC}>{errors.name.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Mobile *</label>
              <input {...register('mobile')} maxLength={10} className={field}/>
              {errors.mobile && <p className={errC}>{errors.mobile.message}</p>}
            </div>
            <div>
              <label className={lbl}>Primary Role *</label>
              <select {...register('role')} className={field}>
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>

          {watchRole === 'doctor' && (
            <div>
              <label className={lbl}>Specialization</label>
              <input {...register('specialization')} placeholder="General Medicine" className={field}/>
            </div>
          )}
          {(watchRole === 'doctor' || watchRole === 'nurse') && (
            <div>
              <label className={lbl}>Registration No.</label>
              <input {...register('regNumber')} placeholder="MH-2024-12345" className={field}/>
            </div>
          )}

          {watchRole === 'doctor' && (
            <div>
              <label className={lbl}>Prescription Mode</label>
              <div className="flex gap-3 mt-1">
                {PRESCRIPTION_MODES.map(m => {
                  const checked = watch('prescriptionMode') === m.value || (!watch('prescriptionMode') && m.value === 'MANUAL');
                  return (
                    <label
                      key={m.value}
                      className={clsx(
                        'flex-1 flex flex-col gap-1 p-3 rounded-lg border-2 cursor-pointer transition-colors',
                        checked
                          ? 'border-navy bg-navy/5 text-navy'
                          : 'border-gray-200 text-gray-500 hover:border-gray-300',
                      )}
                    >
                      <input
                        type="radio"
                        value={m.value}
                        {...register('prescriptionMode')}
                        className="sr-only"
                      />
                      <span className="text-sm font-semibold">{m.label}</span>
                      <span className="text-xs text-gray-400">{m.description}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className={lbl}>Additional Roles
              <span className="ml-1 font-normal text-gray-400 text-xs">
                (click to toggle — user gets permissions from all selected roles)
              </span>
            </label>
            <RoleChips
              primaryRole={watchRole}
              selected={[...additionalRoles, watchRole]}
              onChange={setAdditional}
            />
          </div>

          {apiError && (
            <div className="bg-red-50 border border-red-200 text-clinical-danger text-sm rounded-lg px-4 py-3">
              {apiError}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-lg border border-gray-200 text-navy text-sm font-medium hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={mut.isPending}
              className="flex-1 py-2.5 rounded-lg bg-orange text-navy font-semibold text-sm
                         hover:bg-orange-dark disabled:opacity-50 flex items-center justify-center gap-2">
              {mut.isPending && <Loader2 size={15} className="animate-spin"/>}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────
export default function UsersPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [editUser,   setEditUser]   = useState<any | null>(null);
  const [apiError,   setApiError]   = useState<string | null>(null);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn:  () => api.get('/users').then(r => r.data),
  });

  const users: any[]     = data?.users    ?? [];
  const maxUsers: number = data?.maxUsers ?? 5;

  const createMutation = useMutation({
    mutationFn: (body: object) => api.post('/users', body),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setShowCreate(false);
      reset();
      setApiError(null);
    },
    onError: (e: any) => setApiError(e.response?.data?.error ?? 'Failed to create user'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/users/${id}/status`, { isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const [createAdditional, setCreateAdditional] = useState<string[]>([]);

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: { role: 'doctor' },
  });

  const watchRole = watch('role');

  function onSubmit(values: CreateForm) {
    setApiError(null);
    const extra = createAdditional.filter(r => r !== values.role);
    createMutation.mutate({ ...values, additionalRoles: extra });
  }

  const field = 'w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange';
  const lbl   = 'block text-sm font-medium text-navy mb-1.5';
  const errC  = 'text-clinical-danger text-xs mt-1';

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy">Users</h1>
          <p className="text-gray-400 text-sm mt-0.5">
            {users.length} / {maxUsers} users · {maxUsers - users.length} slots remaining
          </p>
        </div>
        <button
          onClick={() => { setShowCreate(true); setApiError(null); setCreateAdditional([]); }}
          disabled={users.length >= maxUsers}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-orange text-navy
                     font-semibold text-sm hover:bg-orange-dark transition-colors
                     disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={16}/> Add User
        </button>
      </div>

      {/* Slot bar */}
      <div>
        <div className="flex justify-between text-xs text-gray-400 mb-1.5">
          <span>User slots</span>
          <span>{users.length} / {maxUsers}</span>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={clsx('h-full rounded-full transition-all',
              users.length >= maxUsers ? 'bg-clinical-danger' : 'bg-orange')}
            style={{ width: `${Math.min((users.length / maxUsers) * 100, 100)}%` }}
          />
        </div>
        {users.length >= maxUsers && (
          <p className="text-clinical-danger text-xs mt-1">
            User limit reached. Contact BBS Admin to upgrade your plan.
          </p>
        )}
      </div>

      {/* User list */}
      {isLoading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">
          <Loader2 size={20} className="animate-spin mx-auto mb-2 text-orange"/>
          Loading…
        </div>
      ) : users.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex flex-col items-center gap-3">
          <Users size={32} className="text-gray-200"/>
          <p className="text-sm text-gray-400">No staff added yet</p>
          <button onClick={() => setShowCreate(true)} className="text-orange text-sm hover:underline font-medium">
            Add your first user →
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Name', 'Email', 'Roles', 'Status', ''].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((u: any) => {
                const additionalRoles: string[] = (() => {
                  try {
                    const ar = u.additional_roles;
                    return Array.isArray(ar) ? ar : JSON.parse(ar ?? '[]');
                  } catch { return []; }
                })();
                return (
                  <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center
                                        text-navy text-xs font-semibold flex-shrink-0">
                          {u.name.split(' ').slice(0, 2).map((n: string) => n[0]).join('').toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium text-navy">{u.name}</div>
                          {u.specialization && <div className="text-xs text-gray-400">{u.specialization}</div>}
                          <div className="text-xs text-gray-400">{u.mobile}</div>
                          {u.role === 'doctor' && (
                            <span className={clsx(
                              'inline-flex px-1.5 py-0.5 rounded text-xs font-medium mt-0.5',
                              u.prescription_mode === 'DIGITAL'
                                ? 'bg-blue-50 text-blue-600'
                                : 'bg-amber-50 text-amber-700',
                            )}>
                              {u.prescription_mode === 'DIGITAL' ? 'Digital Rx' : 'Manual Rx'}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{u.email}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        <span className={clsx(
                          'inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize',
                          ROLE_COLORS[u.role] ?? 'bg-gray-100 text-gray-500'
                        )}>
                          {roleLabel(u.role)}
                        </span>
                        {additionalRoles.map(r => (
                          <span key={r} className={clsx(
                            'inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize opacity-80',
                            ROLE_COLORS[r] ?? 'bg-gray-100 text-gray-500'
                          )}>
                            {roleLabel(r)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={clsx(
                        'inline-flex px-2 py-0.5 rounded-full text-xs font-medium',
                        u.is_active ? 'bg-clinical-success/10 text-clinical-success' : 'bg-gray-100 text-gray-400'
                      )}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {u.role !== 'hospital_admin' && (
                          <>
                            <button
                              onClick={() => setEditUser(u)}
                              title="Edit user"
                              className="text-gray-400 hover:text-orange transition-colors p-1">
                              <Pencil size={14}/>
                            </button>
                            <button
                              onClick={() => toggleMutation.mutate({ id: u.id, isActive: !u.is_active })}
                              title={u.is_active ? 'Deactivate' : 'Activate'}
                              className="text-gray-400 hover:text-navy transition-colors p-1">
                              {u.is_active ? <UserX size={15}/> : <UserCheck size={15}/>}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit modal */}
      {editUser && <EditUserModal user={editUser} onClose={() => setEditUser(null)}/>}

      {/* Create User modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-bold text-navy">Add Staff Member</h2>
              <button onClick={() => { setShowCreate(false); reset(); setApiError(null); }}
                className="text-gray-400 hover:text-navy">
                <X size={20}/>
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className={lbl}>Full Name *</label>
                  <input {...register('name')} placeholder="Dr. Priya Sharma" className={field}/>
                  {errors.name && <p className={errC}>{errors.name.message}</p>}
                </div>
                <div>
                  <label className={lbl}>Email *</label>
                  <input {...register('email')} type="email" placeholder="priya@hospital.in" className={field}/>
                  {errors.email && <p className={errC}>{errors.email.message}</p>}
                </div>
                <div>
                  <label className={lbl}>Mobile *</label>
                  <input {...register('mobile')} placeholder="9876543210" maxLength={10} className={field}/>
                  {errors.mobile && <p className={errC}>{errors.mobile.message}</p>}
                </div>
                <div>
                  <label className={lbl}>Primary Role *</label>
                  <select {...register('role')} className={field}>
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                {watchRole === 'doctor' && (
                  <div>
                    <label className={lbl}>Specialization</label>
                    <input {...register('specialization')} placeholder="General Medicine" className={field}/>
                  </div>
                )}
                {(watchRole === 'doctor' || watchRole === 'nurse') && (
                  <div className="col-span-2">
                    <label className={lbl}>Registration No.</label>
                    <input {...register('regNumber')} placeholder="MH-2024-12345" className={field}/>
                  </div>
                )}
                {watchRole === 'doctor' && (
                  <div className="col-span-2">
                    <label className={lbl}>Prescription Mode</label>
                    <div className="flex gap-3 mt-1">
                      {PRESCRIPTION_MODES.map(m => {
                        const checked = watch('prescriptionMode') === m.value || (!watch('prescriptionMode') && m.value === 'MANUAL');
                        return (
                          <label
                            key={m.value}
                            className={clsx(
                              'flex-1 flex flex-col gap-1 p-3 rounded-lg border-2 cursor-pointer transition-colors',
                              checked
                                ? 'border-navy bg-navy/5 text-navy'
                                : 'border-gray-200 text-gray-500 hover:border-gray-300',
                            )}
                          >
                            <input
                              type="radio"
                              value={m.value}
                              {...register('prescriptionMode')}
                              className="sr-only"
                            />
                            <span className="text-sm font-semibold">{m.label}</span>
                            <span className="text-xs text-gray-400">{m.description}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className={lbl}>Additional Roles
                  <span className="ml-1 font-normal text-gray-400 text-xs">
                    (user inherits permissions from all selected roles)
                  </span>
                </label>
                <RoleChips
                  primaryRole={watchRole}
                  selected={[...createAdditional, watchRole]}
                  onChange={setCreateAdditional}
                />
              </div>

              <div>
                <label className={lbl}>Temporary Password *</label>
                <input {...register('password')} type="password" placeholder="Min. 8 characters" className={field}/>
                {errors.password && <p className={errC}>{errors.password.message}</p>}
                <p className="text-xs text-gray-400 mt-1">User should change this on first login.</p>
              </div>

              {apiError && (
                <div className="bg-red-50 border border-red-200 text-clinical-danger text-sm rounded-lg px-4 py-3">
                  {apiError}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowCreate(false); reset(); }}
                  className="flex-1 py-2.5 rounded-lg border border-gray-200 text-navy text-sm font-medium hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" disabled={createMutation.isPending}
                  className="flex-1 py-2.5 rounded-lg bg-orange text-navy font-semibold text-sm
                             hover:bg-orange-dark disabled:opacity-50 flex items-center justify-center gap-2">
                  {createMutation.isPending && <Loader2 size={15} className="animate-spin"/>}
                  Add User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
